import { Meter } from '../../ui';
import { calendarDate, num } from '../../lib/format';
import { certificatePath } from '../../lib/publicCertificate';
import type { Certificates as CertificatesData, CertificateProgress } from '../../lib/certificates';
import { useLocale, useStrings } from '../../state/useLocale';

/**
 * The certificates on the map: five small cards between the standing strip
 * and the ledger, each with how many of its checkpoints are passed and what
 * state it is in (docs/certificates.md).
 *
 * ## Why here, and why this small
 *
 * They are part of the learner's standing — a record of what the account has
 * done, next to the rank and the counts — so they sit under that strip and
 * are drawn in its vocabulary: a mono label, a figure, the same thumbnail
 * meter the world headers use. Not a banner. The ledger is what this page is
 * for, and a strip that pushed it down by a screen would be the certificates
 * selling themselves to somebody who came back to play a level.
 *
 * ## Who sees it
 *
 * Signed-in accounts only, owners and free alike. A certificate is a record of
 * an account's passes, and a signed-out browser has none to count — every
 * checkpoint asks for an account before it can be passed. The one sentence a
 * signed-out reader could be told is "certificates come with the course", and
 * saying what a certificate is worth needs the sentence about where it was
 * graded, which the course never says (strings/checkpoint.ts); the public
 * certificate page is where that is argued. So a signed-out map is unchanged.
 *
 * A free account sees its real progress and one line saying certificates are
 * included with the course and that passes made now will count. That is the
 * whole of the pitch here; the offer block at the foot of the page carries
 * the rest, next to the price (components/home/Offer.tsx).
 */
export function Certificates({ value }: { value: CertificatesData | null }) {
  const strings = useStrings();
  const s = strings.certificateCourse.strip;
  const names = strings.certificate.names;
  const { locale } = useLocale();

  // Nothing while the answer is on its way, and nothing if it never came:
  // the strip is an addition to the page, and an empty frame waiting for it
  // would be a layout that moves under the reader.
  if (!value || value.certificates.length === 0) return null;

  const card = (c: CertificateProgress) => {
    const name = names[c.certificate];
    const total = c.missions.length;
    const passed = c.state === 'in-progress' ? c.passed.length : total;
    const left = total - passed;
    const status =
      c.state === 'issued' && c.issuedAt !== undefined
        ? s.issued(calendarDate(new Date(c.issuedAt), locale))
        : c.state === 'revoked'
          ? s.revoked
          : left === 0
            ? // Every checkpoint passed and not issued: the course is what is
              // missing. On an owner this cannot last past the read that just
              // happened, which issues it, so it is said for a free account.
              s.allPassedFree
            : s.toGo(left);
    return (
      <li className={`cert is-${c.state}`} key={c.certificate}>
        <span className="cert-name">{name}</span>
        <span className="cert-figure num">
          <em>{num(passed, locale)}</em>/{num(total, locale)}
        </span>
        <Meter pct={total > 0 ? (passed / total) * 100 : 0} label={s.meterLabel(name, passed, total)} />
        <span className="cert-status note">{status}</span>
        {/* Issued or revoked, the id is a link to its page: a revoked
            certificate's page says it is no longer valid, and that is the
            page a link somebody already holds opens. An absolute path, so the
            app's own click handler leaves it to the browser. */}
        {c.id && c.state !== 'in-progress' && (
          <a className="cert-id" href={certificatePath(c.id)} dir="ltr" aria-label={s.view(name, c.id)}>
            {c.id}
          </a>
        )}
      </li>
    );
  };

  return (
    <section className="certs" aria-labelledby="certs-title">
      <div className="certs-head">
        <h2 className="label" id="certs-title">
          {s.label}
        </h2>
        <p className="note">{value.owns ? s.noteOwner : s.noteFree}</p>
      </div>
      <ol className="certs-list">{value.certificates.map(card)}</ol>
    </section>
  );
}

export default Certificates;
